import { blockCategories } from '../categories';
import { defaultToolbar } from '../bootstrap-components/utils/helpers';
import { configDeviceList } from '../../config/devicesList';
import { resolveDeviceId } from '../../config/legacyDeviceMigration';
import {
    openDynamicLayoutModal,
    normalizeLayoutConfig,
    DEFAULT_LAYOUT_CONFIG,
} from '../../services/dynamicLayoutModal';

const ROW_TYPE = 'dynamic-row';
const CELL_TYPE = 'dynamic-cell';
const ROW_CLASS = 'sio-dynamic-row';
const CELL_CLASS = 'sio-dynamic-cell';
const LAYOUT_SETTINGS_COMMAND = 'dynamic-layout:open-settings';

const ALIGN_ITEMS = {
    start: 'start',
    center: 'center',
    end: 'end',
    stretch: 'stretch',
};
const JUSTIFY_CONTENT = {
    start: 'start',
    center: 'center',
    end: 'end',
    between: 'space-between',
    around: 'space-around',
    evenly: 'space-evenly',
};

const readConfig = (component) => {
    const raw = component.getAttributes()['data-layout'];
    let parsed;
    try {
        parsed = raw ? JSON.parse(raw) : null;
    } catch (_) {
        parsed = null;
    }
    return normalizeLayoutConfig(parsed || DEFAULT_LAYOUT_CONFIG);
};

const getDevicePriority = (deviceId) => {
    const device = configDeviceList.devices.find((d) => d.id === deviceId);
    return device ? device.priority : 0;
};

const shouldStackForDevice = (stackBelow, currentDeviceId) => {
    if (!stackBelow || stackBelow === 'none') return false;
    return getDevicePriority(currentDeviceId) >= getDevicePriority(stackBelow);
};

const applyPreviewStyle = (viewEl, config, currentDeviceId) => {
    if (!viewEl) return;
    const stacked = shouldStackForDevice(config.stackBelow, currentDeviceId);
    viewEl.style.display = 'grid';
    viewEl.style.gridTemplateColumns = stacked
        ? '1fr'
        : config.columns.map((w) => `${w}fr`).join(' ');
    viewEl.style.gap = `${config.gap}px`;
    viewEl.style.alignItems = ALIGN_ITEMS[config.valign] || 'stretch';
    viewEl.style.justifyContent = JUSTIFY_CONTENT[config.halign] || 'start';
};

const openLayoutSettings = (component) => {
    if (!component || typeof component.applyLayoutConfig !== 'function') return;
    openDynamicLayoutModal(readConfig(component)).then((config) => {
        if (config) component.applyLayoutConfig(config);
    });
};

export default (editor) => {
    const domc = editor.DomComponents;
    const bm = editor.BlockManager;
    const tm = editor.TraitManager;

    editor.Commands.add(LAYOUT_SETTINGS_COMMAND, {
        run(ed) {
            openLayoutSettings(ed.getSelected());
        },
    });

    // Trait: a button that opens the layout popup.
    tm.addType('dynamic-layout-editor', {
        noLabel: true,
        createInput({ trait }) {
            const component = trait.target;
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = 'Layout bearbeiten';
            button.style.cssText =
                'width:100%;margin:4px 0;padding:8px 12px;font-size:13px;cursor:pointer;' +
                'background:#0991b2;color:#fff;border:none;border-radius:4px;';
            button.addEventListener('click', () => openLayoutSettings(component));
            return button;
        },
    });

    // The column cell.
    domc.addType(CELL_TYPE, {
        isComponent(el) {
            if (el?.classList?.contains(CELL_CLASS)) {
                return { type: CELL_TYPE };
            }
        },
        model: {
            defaults: {
                tagName: 'div',
                name: 'Spalte',
                draggable: `.${ROW_CLASS}`,
                droppable: true,
                attributes: { class: CELL_CLASS },
            },
        },
    });

    // The row / container.
    domc.addType(ROW_TYPE, {
        isComponent(el) {
            if (el?.classList?.contains(ROW_CLASS)) {
                return { type: ROW_TYPE };
            }
        },
        model: {
            defaults: {
                tagName: 'div',
                name: 'Dynamisches Layout',
                draggable: true,
                droppable: `.${CELL_CLASS}`,
                attributes: { class: ROW_CLASS },
                traits: [
                    {
                        type: 'dynamic-layout-editor',
                        name: 'layout-editor',
                    },
                ],
                toolbar: [
                    ...defaultToolbar,
                    {
                        attributes: {
                            class: 'fa fa-cog',
                            title: 'Layout bearbeiten',
                        },
                        command: LAYOUT_SETTINGS_COMMAND,
                    },
                ],
            },

            init() {
                const attrs = this.getAttributes();
                if (!attrs.id) {
                    this.addAttributes({ id: this.getId() });
                }
                if (!attrs['data-layout']) {
                    // Fresh drop: persist defaults, then open the settings modal.
                    this.applyLayoutConfig(readConfig(this), { rebuild: true });
                    this.set('_openLayoutOnRender', true);
                }

                // Ensure settings icon is present even if GrapesJS merges toolbars.
                const toolbar = this.get('toolbar') || [];
                if (!toolbar.some((btn) => btn.command === LAYOUT_SETTINGS_COMMAND)) {
                    this.set('toolbar', [
                        ...toolbar,
                        {
                            attributes: {
                                class: 'fa fa-cog',
                                title: 'Layout bearbeiten',
                            },
                            command: LAYOUT_SETTINGS_COMMAND,
                        },
                    ]);
                }
            },

            applyLayoutConfig(rawConfig, { rebuild = true } = {}) {
                const config = normalizeLayoutConfig(rawConfig);
                this.addAttributes({ 'data-layout': JSON.stringify(config) });

                if (rebuild) {
                    this.syncCells(config.columns.length);
                }

                this.trigger('sio:layout-updated', config);
                this.em?.trigger('component:update', this);
                return config;
            },

            syncCells(count) {
                const children = this.components();
                const current = children.length;

                if (current < count) {
                    for (let i = current; i < count; i += 1) {
                        this.append({ type: CELL_TYPE });
                    }
                } else if (current > count) {
                    const lastKept = children.at(count - 1);
                    // Preserve content: move children of removed cells into
                    // the last remaining cell before dropping them.
                    for (let i = current - 1; i >= count; i -= 1) {
                        const cell = children.at(i);
                        if (lastKept && cell?.components) {
                            cell.components().forEach((child) => {
                                lastKept.append(child.clone());
                            });
                        }
                        cell?.remove();
                    }
                }
            },
        },

        view: {
            init() {
                this.listenTo(
                    this.model,
                    'sio:layout-updated',
                    this.renderLayout
                );
                this.onDeviceChange = () => this.renderLayout();
                this.listenTo(editor, 'change:device', this.onDeviceChange);
                this.listenTo(editor, 'device:select', this.onDeviceChange);
            },
            onRender() {
                this.renderLayout();
                this.autoOpenSettings();
            },
            autoOpenSettings() {
                if (this._autoOpenedLayoutModal) return;
                if (!this.model.get('_openLayoutOnRender')) return;

                this._autoOpenedLayoutModal = true;
                this.model.set('_openLayoutOnRender', false, { silent: true });
                openLayoutSettings(this.model);
            },
            renderLayout() {
                if (!this.model || !this.el) return;
                const deviceId =
                    resolveDeviceId(editor.getDevice()) ||
                    configDeviceList.default;
                applyPreviewStyle(
                    this.el,
                    readConfig(this.model),
                    deviceId
                );
            },
        },
    });

    bm.add('dynamic-layout', {
        label: 'Dynamisches Layout',
        category: blockCategories.layout,
        select: true,
        content: { type: ROW_TYPE },
        media: `<svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 4a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4Zm2 1v14h3V5H5Zm7-1a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V4Zm2 1v14h5V5h-5Z"/>
        </svg>`,
    });
};
